// localStorage-backed program shortlist, with cross-component sync and a
// 30-day rolling expiry so users who return within a month still see their
// saved programs, but stale data eventually clears out.
//
// Entries: { slug, name, delivery, industry, durationWeeks, heroImage }
import { useCallback, useEffect, useState } from "react";

const KEY = "ac-shortlist";
const META_KEY = "ac-shortlist-meta";
const EVT = "ac-shortlist-change";
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const now = () => Date.now();

const readMeta = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeMeta = (meta) => {
  try {
    window.localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* quota — ignore */
  }
};

const isExpired = (meta) =>
  meta && meta.updated_at && now() - meta.updated_at > TTL_MS;

const read = () => {
  if (typeof window === "undefined") return [];
  try {
    const meta = readMeta();
    if (isExpired(meta)) {
      // Stale → clear and return empty
      window.localStorage.removeItem(KEY);
      window.localStorage.removeItem(META_KEY);
      return [];
    }
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const write = (list) => {
  window.localStorage.setItem(KEY, JSON.stringify(list));
  // Touch the meta record so the 30-day window rolls forward on every
  // add/remove. "Active user" = continues to see their shortlist.
  writeMeta({ updated_at: now(), count: list.length });
  window.dispatchEvent(new CustomEvent(EVT));
};

export function useShortlist() {
  const [items, setItems] = useState(read);

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    // Also re-check on mount in case the last visit was >30 days ago.
    sync();
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const has = useCallback((slug) => items.some((i) => i.slug === slug), [items]);

  const add = useCallback((entry) => {
    const list = read();
    if (list.some((i) => i.slug === entry.slug)) return;
    write([...list, entry]);
  }, []);

  const remove = useCallback((slug) => {
    write(read().filter((i) => i.slug !== slug));
  }, []);

  const toggle = useCallback((entry) => {
    const list = read();
    if (list.some((i) => i.slug === entry.slug)) write(list.filter((i) => i.slug !== entry.slug));
    else write([...list, entry]);
  }, []);

  const clear = useCallback(() => write([]), []);

  return { items, count: items.length, has, add, remove, toggle, clear };
}

/**
 * Non-hook accessor for non-React contexts (e.g. form submit handlers that
 * read the shortlist without subscribing to re-renders).
 */
export function getShortlistSnapshot() {
  return read();
}
